import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/theme/useTheme';

interface HighlightFeature {
  icon: string;
  label: string;
  color: string;
}

interface EntrepreneurCta {
  href: string;
  label: string;
  icon: string;
  trailingIcon: string;
}

interface SectionLink {
  badge: string;
  title: string;
  description: string;
  href: string;
  buttonLabel: string;
  icon: string;
  buttonIcon: string;
}

const sectionGradientLight = 'linear-gradient(145deg, #F8FAFF 0%, #EEF4FF 50%, #FFF0F3 100%)';
const sectionGradientDark = 'linear-gradient(145deg, #06111f 0%, #081424 50%, #0d1b31 100%)';
const panelGradient = 'linear-gradient(145deg, #091D42 0%, #0D2660 60%, #112870 100%)';

const glowStyles = {
  mainTop: 'rgba(0,196,240,0.15)',
  mainBottom: 'rgba(232,54,93,0.12)',
};

interface SectionBlockProps {
  data: SectionLink;
  accentLine: string;
  glowTop: string;
  glowBottom: string;
  bottomAccent: string;
  fillColor: string;
}

const SectionBlockCard = ({
  data,
  accentLine,
  glowTop,
  glowBottom,
  bottomAccent,
  fillColor,
}: SectionBlockProps) => (
  <div className="mt-8 max-w-4xl mx-auto">
    <div
      className="rounded-3xl overflow-hidden relative"
      style={{ background: panelGradient }}
    >
      <div className="h-0.5" style={{ background: accentLine }} />

      <div className="absolute top-0 right-0 w-52 h-52 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: glowTop, transform: 'translate(25%, -25%)' }} />
      <div className="absolute bottom-0 left-0 w-44 h-44 rounded-full blur-3xl opacity-25 pointer-events-none" style={{ background: glowBottom, transform: 'translate(-25%, 25%)' }} />

      <div className="relative z-10 p-8 sm:p-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${fillColor}22`, border: `1px solid ${fillColor}55` }}
            >
              <div className="w-7 h-7 flex items-center justify-center">
                <i className={`${data.icon} text-2xl`} style={{ color: fillColor }} />
              </div>
            </div>
            <div>
              <div
                className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1 rounded-full mb-2 tracking-widest uppercase"
                style={{ background: `${fillColor}15`, color: fillColor }}
              >
                {data.badge}
              </div>
              <h3 className="text-white font-black text-lg sm:text-xl" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900 }}>
                {data.title}
              </h3>
              <p className="text-white/50 text-sm mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                {data.description}
              </p>
            </div>
          </div>
          <a
            href={data.href}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="cursor-pointer whitespace-nowrap flex-shrink-0 font-bold px-7 py-3.5 rounded-full transition-all duration-300 text-sm inline-flex items-center gap-2"
            style={{ background: fillColor, color: 'white', fontFamily: "'Inter', sans-serif" }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = '#009DC0';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = fillColor;
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${data.buttonIcon} text-sm`} />
            </div>
            {data.buttonLabel}
          </a>
        </div>
      </div>

      <div className="h-0.5" style={{ background: bottomAccent }} />
    </div>
  </div>
);

const EntrepreneurSection = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [inView, setInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const highlightsRaw = t('home.entrepreneur.highlights', { returnObjects: true });
  const highlights = Array.isArray(highlightsRaw)
    ? (highlightsRaw as HighlightFeature[])
    : [];
  const cta = t('home.entrepreneur.cta', { returnObjects: true }) as EntrepreneurCta;
  const pitch = t('home.entrepreneur.pitch', { returnObjects: true }) as SectionLink;
  const faq = t('home.entrepreneur.faq', { returnObjects: true }) as SectionLink;
  const materials = t('home.entrepreneur.materials', { returnObjects: true }) as SectionLink;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const sectionBackground = isDark ? sectionGradientDark : sectionGradientLight;

  return (
    <section
      id="painel"
      ref={sectionRef}
      className="w-full py-16 sm:py-24 px-4 overflow-hidden relative"
      style={{ background: sectionBackground }}
    >
      <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: glowStyles.mainTop, transform: 'translate(25%, -25%)' }} />
      <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full blur-3xl opacity-25 pointer-events-none" style={{ background: glowStyles.mainBottom, transform: 'translate(-25%, 25%)' }} />

      <style>{`
        @keyframes panelPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,196,240,0); }
          50% { box-shadow: 0 0 0 14px rgba(0,196,240,0.06); }
        }
        @keyframes liveBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }
        @keyframes entryFadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes btnGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,196,240,0.0), 0 4px 20px rgba(0,196,240,0.25); }
          50% { box-shadow: 0 0 0 8px rgba(0,196,240,0.12), 0 8px 32px rgba(0,196,240,0.45); }
        }
        @keyframes btnShimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto relative z-10">

        <div className="flex justify-center mb-6">
          <div className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full tracking-widest uppercase border ${isDark ? 'border-white/20 text-white' : 'border-[#E0EAF4] text-[#091D42]'}`}>
            <i className="ri-user-star-line text-[#E8365D]" />
            {t('home.entrepreneur.badge')}
          </div>
        </div>

        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: panelGradient,
            animation: inView ? 'panelPulse 4s ease-in-out infinite' : 'none',
          }}
        >
          <div className="h-0.5" style={{ background: 'linear-gradient(90deg, transparent, #E8365D88, #E8365D, #E8365D88, transparent)' }} />

          <div className="p-8 sm:p-12">
            <div className="flex flex-col items-center text-center gap-8">

              <div className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#E8365D]" style={{ animation: 'liveBlink 1.6s ease-in-out infinite' }} />
                <span className="text-[#E8365D] text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {t('home.entrepreneur.liveLabel')}
                </span>
              </div>

              <h2
                className="text-white font-black text-3xl sm:text-4xl lg:text-5xl leading-tight"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900 }}
              >
                {t('home.entrepreneur.titleLine1')}
                <br />
                <span style={{ background: 'linear-gradient(90deg, #00C4F0, #4FC3F7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  {t('home.entrepreneur.titleHighlight')}
                </span>
              </h2>

              <p
                className="text-white/55 text-base sm:text-lg leading-relaxed max-w-md"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {t('home.entrepreneur.description')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-2xl">
                {highlights.map((feat, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center gap-3 p-4 rounded-2xl border transition-all duration-300 cursor-default"
                    style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(0,196,240,0.14)' }}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${feat.color}22` }}>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${feat.icon} text-lg`} style={{ color: feat.color }} />
                      </div>
                    </div>
                    <span className="text-white/70 text-sm font-medium text-center" style={{ fontFamily: "'Inter', sans-serif" }}>{feat.label}</span>
                  </div>
                ))}
              </div>

              <div className="w-full flex justify-center">
                <a
                  href={cta.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group relative inline-flex items-center justify-center gap-3 font-black px-10 py-5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-base w-full sm:w-auto overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #00C4F0 0%, #0099CC 100%)',
                    color: '#060D1A',
                    fontFamily: "'Montserrat', sans-serif",
                    animation: inView ? 'btnGlow 2.4s ease-in-out infinite' : 'none',
                    letterSpacing: '0.01em',
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    el.style.background = 'linear-gradient(135deg, #E8365D 0%, #C42E4D 100%)';
                    el.style.color = 'white';
                    el.style.transform = 'scale(1.06)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    el.style.background = 'linear-gradient(135deg, #00C4F0 0%, #0099CC 100%)';
                    el.style.color = '#060D1A';
                    el.style.transform = 'scale(1)';
                  }}
                >
                  <span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.28) 50%, transparent 60%)',
                      backgroundSize: '200% 100%',
                      animation: inView ? 'btnShimmer 2.8s linear infinite' : 'none',
                    }}
                  />
                  <div className="w-5 h-5 flex items-center justify-center relative z-10">
                    <i className={`${cta.icon} text-base`} />
                  </div>
                  <span className="relative z-10">{cta.label}</span>
                  <div className="w-5 h-5 flex items-center justify-center relative z-10">
                    <i className={`${cta.trailingIcon} text-base`} />
                  </div>
                </a>
              </div>

            </div>
          </div>

          <div className="h-0.5" style={{ background: 'linear-gradient(90deg, #00C4F022, #00C4F0, #00C4F022)' }} />
        </div>

        <SectionBlockCard
          data={pitch}
          accentLine="linear-gradient(90deg, transparent, #E8365D88, #E8365D, #E8365D88, transparent)"
          glowTop="rgba(0,196,240,0.08)"
          glowBottom="rgba(0,196,240,0.08)"
          bottomAccent="linear-gradient(90deg, #00C4F022, #00C4F0, #00C4F022)"
          fillColor="#00C4F0"
        />

        <SectionBlockCard
          data={faq}
          accentLine="linear-gradient(90deg, transparent, #00C4F088, #00C4F0, #00C4F088, transparent)"
          glowTop="rgba(0,196,240,0.08)"
          glowBottom="rgba(232,54,93,0.08)"
          bottomAccent="linear-gradient(90deg, #00C4F022, #00C4F0, #00C4F022)"
          fillColor="#00C4F0"
        />

        <SectionBlockCard
          data={materials}
          accentLine="linear-gradient(90deg, transparent, #4FC3F788, #4FC3F7, #4FC3F788, transparent)"
          glowTop="rgba(79,195,247,0.08)"
          glowBottom="rgba(0,196,240,0.08)"
          bottomAccent="linear-gradient(90deg, #4FC3F722, #4FC3F7, #4FC3F722)"
          fillColor="#4FC3F7"
        />

      </div>
    </section>
  );
};

export default EntrepreneurSection;
